import {
  parseStoreSkuKey,
  QTY_FIELDS,
  resolveLineSettings,
  SETTING_FIELDS,
  storeSkuKey,
  type QtyField,
  type ResolvedLineSettings,
  type SettingField,
  type StoreSkuCell,
  type StoreSkuSettingValues,
  type VariantSettingValues,
} from "~/utils/allocationLineSettings"
import { MAX_PRODUCT_SETTING_ROWS } from "~/utils/suggestedQuantity"
import { chunk } from "~/utils/tsUtils"
import { CLASSIFICATIONS, type StoreClassification } from "../../types"
import type { StoreWithStatus } from "../../useAllocationData"

// Settings › Products: saved settings merged from the batched reads, the staged
// draft over them, and what each store x size cell resolves to. Pure.

/** Saved settings of every listed variant: store x SKU rows by storeSkuKey. */
export type SavedProductSettings = {
  variants: ReadonlyMap<string, VariantSettingValues>
  storeSkus: ReadonlyMap<string, StoreSkuSettingValues>
  /** The locations each SKU has a saved store x SKU row at, so a summary visits only those. */
  locationsBySku: ReadonlyMap<string, readonly string[]>
}

type SettingsRead = {
  variants: (VariantSettingValues & { variantId: string })[]
  storeSkus: (StoreSkuSettingValues & StoreSkuCell)[]
}

/** The variant id batches one read each: deduplicated and sorted, so the batches stay stable. */
export function variantIdBatches(variantIds: Iterable<string>): string[][] {
  return chunk([...new Set(variantIds)].sort(), MAX_PRODUCT_SETTING_ROWS)
}

/** One SavedProductSettings from the batched reads. Null until every batch is in. */
export function mergeSettingsReads(
  reads: (SettingsRead | undefined)[],
): SavedProductSettings | null {
  const loaded = reads.filter((read): read is SettingsRead => read !== undefined)
  if (loaded.length === 0 || loaded.length < reads.length) return null
  const variants = new Map<string, VariantSettingValues>()
  const storeSkus = new Map<string, StoreSkuSettingValues>()
  for (const read of loaded) {
    for (const { variantId, minDisplayQty, maxQty, orderMultiple } of read.variants) {
      variants.set(variantId, { minDisplayQty, maxQty, orderMultiple })
    }
    for (const row of read.storeSkus) {
      const { excluded, minDisplayQty, maxQty, orderMultiple } = row
      storeSkus.set(storeSkuKey(row), { excluded, minDisplayQty, maxQty, orderMultiple })
    }
  }
  return {
    variants,
    storeSkus,
    locationsBySku: locationsBySku(storeSkus.keys()),
  }
}

const NO_VARIANT_MESSAGE = "No variant matches the search and filters."
const READ_FAILED_MESSAGE =
  "The product settings could not be loaded. Reload the page to try again."

/** Loading ends once every batch is in or one has failed for good; a failed read then explains itself. */
export function productListState(input: {
  basicsLoading: boolean
  batchCount: number
  saved: SavedProductSettings | null
  isError: boolean
}): { isLoading: boolean; emptyMessage: string } {
  const waitingForBatches = input.batchCount > 0 && !input.saved && !input.isError
  return {
    isLoading: input.basicsLoading || waitingForBatches,
    emptyMessage: input.isError ? READ_FAILED_MESSAGE : NO_VARIANT_MESSAGE,
  }
}

/** One variant's staged edits: its own row, and its store x SKU cells by storeSkuKey. */
export type VariantDraft = {
  variant?: Partial<VariantSettingValues>
  cells: Record<string, Partial<StoreSkuSettingValues>>
}

/** Staged edits by variant: a value sets it, null clears it (the level above applies). */
export type ProductSettingsDraft = Record<string, VariantDraft>

export const EMPTY_PRODUCT_SETTINGS_DRAFT: ProductSettingsDraft = {}

const NO_VARIANT_ROW: VariantSettingValues = {
  minDisplayQty: null,
  maxQty: null,
  orderMultiple: null,
}
const NO_STORE_SKU_ROW: StoreSkuSettingValues = { excluded: null, ...NO_VARIANT_ROW }

// Not excluded is the default, so false is no override, the same as the server stores it.
const normalized = (field: SettingField, value: number | boolean | null | undefined) =>
  field === "excluded" && value === false ? null : (value ?? null)

/** The patch with one field staged; undefined once it holds nothing the saved row lacks. */
function restaged(
  patch: Partial<StoreSkuSettingValues> | undefined,
  field: SettingField,
  value: number | boolean | null,
  saved: Partial<StoreSkuSettingValues> | undefined,
): Partial<StoreSkuSettingValues> | undefined {
  const { [field]: _dropped, ...rest } = patch ?? {}
  const next =
    normalized(field, value) === normalized(field, saved?.[field])
      ? rest
      : { ...rest, [field]: normalized(field, value) }
  return Object.keys(next).length === 0 ? undefined : next
}

/** The draft with one variant's entry replaced; the other entries keep their identity. */
function withVariantDraft(
  draft: ProductSettingsDraft,
  variantId: string,
  variant: Partial<VariantSettingValues> | undefined,
  cells: VariantDraft["cells"],
): ProductSettingsDraft {
  if (variant === undefined && Object.keys(cells).length === 0) {
    const { [variantId]: _dropped, ...others } = draft
    return others
  }
  return { ...draft, [variantId]: variant === undefined ? { cells } : { variant, cells } }
}

/** Stages one variant-level field; a value equal to the saved one leaves the draft. */
export function stageVariantField(
  draft: ProductSettingsDraft,
  saved: SavedProductSettings,
  variantId: string,
  field: QtyField,
  value: number | null,
): ProductSettingsDraft {
  const entry = draft[variantId]
  const variant = restaged(entry?.variant, field, value, saved.variants.get(variantId))
  return withVariantDraft(draft, variantId, variant, entry?.cells ?? {})
}

/** Stages one field for each of the variant's cells; a value equal to the saved one leaves the draft. */
export function stageStoreSkuField(
  draft: ProductSettingsDraft,
  saved: SavedProductSettings,
  variantId: string,
  cells: StoreSkuCell[],
  field: SettingField,
  value: number | boolean | null,
): ProductSettingsDraft {
  const entry = draft[variantId]
  // Assigned over a copy, so a re-staged cell keeps its place in Details.
  const staged = { ...entry?.cells }
  for (const cell of cells) {
    const key = storeSkuKey(cell)
    const patch = restaged(staged[key], field, value, saved.storeSkus.get(key))
    if (patch === undefined) delete staged[key]
    else staged[key] = patch
  }
  return withVariantDraft(draft, variantId, entry?.variant, staged)
}

/**
 * Fill a column across every store and size: the value becomes the variant's own (so stores and
 * sizes added later get it too) and the cells' own values for the field go, so every cell shows it.
 */
export function fillVariantField(
  draft: ProductSettingsDraft,
  saved: SavedProductSettings,
  variantId: string,
  field: QtyField,
  value: number,
  cells: StoreSkuCell[],
): ProductSettingsDraft {
  const staged = stageVariantField(draft, saved, variantId, field, value)
  const own = cells.filter(
    (cell) => (storeSkuRowOf(saved, cell, staged[variantId])?.[field] ?? null) !== null,
  )
  return own.length === 0 ? staged : stageStoreSkuField(staged, saved, variantId, own, field, null)
}

/** One variant's staged field edits: one Details row each. */
export function variantDraftChangeCount(entry: VariantDraft | undefined): number {
  if (!entry) return 0
  const cellFields = Object.values(entry.cells).reduce(
    (sum, patch) => sum + Object.keys(patch).length,
    0,
  )
  return Object.keys(entry.variant ?? {}).length + cellFields
}

/** The saved variant row with the draft over it; undefined when neither sets anything. */
export function variantRowOf(
  saved: SavedProductSettings,
  variantId: string,
  entry: VariantDraft | undefined,
): VariantSettingValues | undefined {
  const row = saved.variants.get(variantId)
  if (!entry?.variant) return row
  return { ...(row ?? NO_VARIANT_ROW), ...entry.variant }
}

/** The saved store x SKU row with the variant's draft over it; undefined when neither sets anything. */
export function storeSkuRowOf(
  saved: SavedProductSettings,
  cell: StoreSkuCell,
  entry: VariantDraft | undefined,
): StoreSkuSettingValues | undefined {
  const key = storeSkuKey(cell)
  const row = saved.storeSkus.get(key)
  const patch = entry?.cells[key]
  if (!patch) return row
  return { ...(row ?? NO_STORE_SKU_ROW), ...patch }
}

/** The patch saveProductSettings takes. */
export function draftToPatch(draft: ProductSettingsDraft) {
  const entries = Object.entries(draft)
  return {
    variants: entries.flatMap(([variantId, { variant }]) =>
      variant ? [{ variantId, ...variant }] : [],
    ),
    storeSkus: entries.flatMap(([, { cells }]) =>
      Object.entries(cells).map(([key, patch]) => ({ ...parseStoreSkuKey(key), ...patch })),
    ),
  }
}

/** A row whose own maximum is below its own minimum: only a draft can hold one, and it blocks Save. */
function belowMinimum<R extends VariantSettingValues>(
  row: R | undefined,
): row is R & { minDisplayQty: number; maxQty: number } {
  return (
    row !== undefined &&
    row.minDisplayQty !== null &&
    row.maxQty !== null &&
    row.maxQty < row.minDisplayQty
  )
}

/** Why the draft can't be saved, if it can't: the server checks the same things. */
export function draftSaveBlocker(
  saved: SavedProductSettings,
  draft: ProductSettingsDraft,
): string | undefined {
  const entries = Object.entries(draft)
  const variantCount = entries.filter(([, entry]) => entry.variant !== undefined).length
  const storeSkuCount = entries.reduce((sum, [, entry]) => sum + Object.keys(entry.cells).length, 0)
  if (variantCount > MAX_PRODUCT_SETTING_ROWS || storeSkuCount > MAX_PRODUCT_SETTING_ROWS) {
    return `Save at most ${MAX_PRODUCT_SETTING_ROWS.toLocaleString("en")} store and size changes at once`
  }
  const below = entries.some(
    ([variantId, entry]) =>
      (entry.variant !== undefined && belowMinimum(variantRowOf(saved, variantId, entry))) ||
      Object.keys(entry.cells).some((key) =>
        belowMinimum(storeSkuRowOf(saved, parseStoreSkuKey(key), entry)),
      ),
  )
  if (below) {
    return "A maximum is below the minimum set next to it. Raise the maximum or lower the minimum."
  }
  return undefined
}

/** What a cell follows when its own value is cleared: the variant level, else the default. */
export function inheritedValue<F extends SettingField>(
  row: StoreSkuSettingValues | undefined,
  variant: VariantSettingValues | undefined,
  field: F,
): ResolvedLineSettings[F] {
  const resolved = resolveLineSettings({ ...(row ?? NO_STORE_SKU_ROW), [field]: null }, variant)
  return resolved[field]
}

/** A variant as the Products tab lists it: its sizes, each with the store locations it lists them at. */
export type ListedVariant = {
  variantId: string
  skus: { skuId: string; size: string; locationIds: readonly string[] }[]
}

export type RangeSummary = { low: number; high: number } | null

export type SizeSummary = {
  skuId: string
  size: string
  storeCount: number
  includedCount: number
  excludedCount: number
  /** Over the included stores; maxQty over the capped ones, and `uncapped` says whether any has no maximum. */
  ranges: Record<QtyField, RangeSummary>
  uncapped: boolean
  totalMin: number
  totalMax: number | null
  /** Stores whose minimum is above their maximum, on one level or across levels. */
  conflictCount: number
  overrides: Record<SettingField, number>
}

export type VariantSummary = {
  /** Store x size cells with their own value, per field, plus whether the variant level sets it. */
  overrides: Record<SettingField, number>
  /** Store x size cells that carry a value, their own or the variant's; excluded counts excluded cells. */
  setCells: Record<SettingField, number>
  variantLevel: VariantSettingValues | undefined
  hasOverrides: boolean
  totalMin: number
  totalMax: number | null
  sizes: SizeSummary[]
}

function widen(range: RangeSummary, value: number): RangeSummary {
  if (range === null) return { low: value, high: value }
  return { low: Math.min(range.low, value), high: Math.max(range.high, value) }
}

/** Store x SKU keys grouped as SKU id to its location ids. */
export function locationsBySku(keys: Iterable<string>): Map<string, string[]> {
  const index = new Map<string, string[]>()
  for (const key of keys) {
    const { skuId, locationId } = parseStoreSkuKey(key)
    const locations = index.get(skuId)
    if (locations) locations.push(locationId)
    else index.set(skuId, [locationId])
  }
  return index
}

/** The listed locations where the SKU has a saved row or a staged cell. */
function rowLocations(
  skuId: string,
  listed: readonly string[],
  saved: SavedProductSettings,
  stagedBySku: ReadonlyMap<string, string[]>,
): Set<string> {
  const savedAt = saved.locationsBySku.get(skuId) ?? []
  const stagedAt = stagedBySku.get(skuId) ?? []
  const out = new Set<string>()
  if (savedAt.length === 0 && stagedAt.length === 0) return out
  const listedSet = new Set(listed)
  for (const locationId of savedAt) {
    if (listedSet.has(locationId)) out.add(locationId)
  }
  for (const locationId of stagedAt) {
    if (listedSet.has(locationId)) out.add(locationId)
  }
  return out
}

/**
 * Counts, coverage, ranges and totals for one variant. Excluded cells add nothing to the totals.
 * Cells with no row of their own all resolve alike, so they count in one step: a variant listed
 * at every store costs its rows, not its stores.
 */
export function summarizeVariant(
  variant: ListedVariant,
  saved: SavedProductSettings,
  entry: VariantDraft | undefined,
): VariantSummary {
  const variantLevel = variantRowOf(saved, variant.variantId, entry)
  const overrides: Record<SettingField, number> = {
    excluded: 0,
    minDisplayQty: 0,
    maxQty: 0,
    orderMultiple: 0,
  }
  const setCells: Record<SettingField, number> = {
    excluded: 0,
    minDisplayQty: 0,
    maxQty: 0,
    orderMultiple: 0,
  }
  const base = resolveLineSettings(undefined, variantLevel)
  const stagedBySku = locationsBySku(Object.keys(entry?.cells ?? {}))
  const sizes = variant.skus.map(({ skuId, size, locationIds }): SizeSummary => {
    const summary: SizeSummary = {
      skuId,
      size,
      storeCount: locationIds.length,
      includedCount: 0,
      excludedCount: 0,
      ranges: { minDisplayQty: null, maxQty: null, orderMultiple: null },
      uncapped: false,
      totalMin: 0,
      totalMax: 0,
      conflictCount: 0,
      overrides: { excluded: 0, minDisplayQty: 0, maxQty: 0, orderMultiple: 0 },
    }
    const addIncluded = (
      cell: Pick<ResolvedLineSettings, "minDisplayQty" | "maxQty" | "orderMultiple">,
      conflict: boolean,
      count: number,
    ) => {
      if (conflict) summary.conflictCount += count
      summary.includedCount += count
      const { ranges } = summary
      ranges.minDisplayQty = widen(ranges.minDisplayQty, cell.minDisplayQty)
      ranges.orderMultiple = widen(ranges.orderMultiple, cell.orderMultiple)
      summary.totalMin += cell.minDisplayQty * count
      if (cell.maxQty === null) {
        summary.uncapped = true
      } else {
        ranges.maxQty = widen(ranges.maxQty, cell.maxQty)
        if (summary.totalMax !== null) summary.totalMax += cell.maxQty * count
      }
    }
    const withRows = rowLocations(skuId, locationIds, saved, stagedBySku)
    for (const locationId of withRows) {
      const row = storeSkuRowOf(saved, { skuId, locationId }, entry)
      if (row?.excluded === true) setCells.excluded += 1
      for (const field of QTY_FIELDS) {
        if ((row?.[field] ?? variantLevel?.[field] ?? null) !== null) setCells[field] += 1
      }
      if (row !== undefined) {
        for (const field of SETTING_FIELDS) {
          if (row[field] === null) continue
          overrides[field] += 1
          summary.overrides[field] += 1
        }
      }
      const resolved = row === undefined ? base : resolveLineSettings(row, variantLevel)
      // A row's own min above its own max can't be saved, so show it as staged. Across
      // levels it saves and the engine reconciles, so show the engine's values.
      const ownConflict = belowMinimum(row)
      const cell = ownConflict
        ? { ...resolved, minDisplayQty: row.minDisplayQty, maxQty: row.maxQty }
        : resolved
      if (cell.excluded) {
        summary.excludedCount += 1
        continue
      }
      addIncluded(cell, ownConflict || resolved.settingsConflict !== null, 1)
    }
    const plain = locationIds.length - withRows.size
    if (plain > 0) {
      for (const field of QTY_FIELDS) {
        if ((variantLevel?.[field] ?? null) !== null) setCells[field] += plain
      }
      addIncluded(base, base.settingsConflict !== null, plain)
    }
    if (summary.uncapped) summary.totalMax = null
    return summary
  })
  const variantSets = variantLevel !== undefined && QTY_FIELDS.some((f) => variantLevel[f] !== null)
  return {
    overrides,
    setCells,
    variantLevel,
    hasOverrides: variantSets || SETTING_FIELDS.some((field) => overrides[field] > 0),
    totalMin: sizes.reduce((sum, size) => sum + size.totalMin, 0),
    totalMax: sizes.some((size) => size.totalMax === null)
      ? null
      : sizes.reduce((sum, size) => sum + (size.totalMax ?? 0), 0),
    sizes,
  }
}

export const ALL_STORES_SCOPE = "all"
/** The fill scope of the table's view the dialog was opened from. */
export const VIEW_SCOPE = "view"

/** The table's current view as the dialog's starting fill scope: a store, or a package's stores. */
export type DialogView = { label: string; storeIds: readonly string[] }

type ClassedStore = Pick<StoreWithStatus, "id" | "storeClass">

export const scopeLabel = (storeClass: StoreClassification) =>
  storeClass === "Outlet" ? "Outlet" : `Class ${storeClass}`

/** The current view when there is one (its name on hover), all stores, then each class the stores have. */
export function fillScopeOptions(
  stores: Pick<StoreWithStatus, "storeClass">[],
  view?: DialogView,
): { value: string; label: string; tooltip?: string }[] {
  const classes = new Set(stores.map((store) => store.storeClass))
  return [
    ...(view ? [{ value: VIEW_SCOPE, label: "Current view", tooltip: view.label }] : []),
    { value: ALL_STORES_SCOPE, label: "All stores" },
    ...CLASSIFICATIONS.filter((storeClass) => classes.has(storeClass)).map((storeClass) => ({
      value: storeClass,
      label: scopeLabel(storeClass),
    })),
  ]
}

/** The stores a Fill a column value reaches, and the dialog shows. */
export function storesInScope<S extends ClassedStore>(
  stores: S[],
  scope: string,
  view?: DialogView,
): S[] {
  if (scope === ALL_STORES_SCOPE) return stores
  if (scope === VIEW_SCOPE) {
    const inView = new Set(view?.storeIds)
    return stores.filter((store) => inView.has(store.id))
  }
  return stores.filter((store) => store.storeClass === scope)
}

/** "3", or "1–4" when the stores differ. */
export function formatRange(range: RangeSummary): string {
  if (range === null) return ""
  return range.low === range.high ? `${range.low}` : `${range.low}–${range.high}`
}

/** What a row of the list sums: sizes, store x size overrides per field, excluded cells and the totals. */
export type ListCounts = {
  sizes: number
  minDisplayQty: number
  maxQty: number
  orderMultiple: number
  excluded: number
  totalMin: number
  totalMax: number | null
}

export function variantCounts(summary: VariantSummary): ListCounts {
  return {
    sizes: summary.sizes.length,
    minDisplayQty: summary.setCells.minDisplayQty,
    maxQty: summary.setCells.maxQty,
    orderMultiple: summary.setCells.orderMultiple,
    excluded: summary.sizes.reduce((sum, size) => sum + size.excludedCount, 0),
    totalMin: summary.totalMin,
    totalMax: summary.totalMax,
  }
}

/** A package's or the whole list's counts. One uncapped variant leaves the total max uncapped. */
export function sumCounts(counts: ListCounts[]): ListCounts {
  const sum = (pick: (c: ListCounts) => number) => counts.reduce((total, c) => total + pick(c), 0)
  return {
    sizes: sum((c) => c.sizes),
    minDisplayQty: sum((c) => c.minDisplayQty),
    maxQty: sum((c) => c.maxQty),
    orderMultiple: sum((c) => c.orderMultiple),
    excluded: sum((c) => c.excluded),
    totalMin: sum((c) => c.totalMin),
    totalMax: counts.some((c) => c.totalMax === null) ? null : sum((c) => c.totalMax ?? 0),
  }
}

/** Newest first, at most `limit`, from the changelog's batched reads. */
export function mergeChangelogs<T extends { at: Date }>(batches: T[][], limit: number): T[] {
  return batches
    .flat()
    .sort((a, b) => b.at.getTime() - a.at.getTime())
    .slice(0, limit)
}

export function sameCounts(a: ListCounts, b: ListCounts): boolean {
  return (Object.keys(a) as (keyof ListCounts)[]).every((key) => a[key] === b[key])
}
